import { useNavigate } from "react-router-dom";
import { FileText, Gauge, Compass, ShieldCheck, ArrowRight } from "lucide-react";
import { T, withAlpha } from "../tokens";
import { Btn } from "../components/Btn";
import { Badge } from "../components/Badge";
import { useReveal } from "../hooks/useMotion";
import { useLaunchConfig } from "../lib/launch-config";
import { usePageMeta } from "../lib/page-meta";
import { LegalLinks } from "../components/LegalLinks";

// v1.1 HONEST_COPY_ENABLED: tech-first, "less tested outside tech", no "every role" claims.
// With the flag off every string below is the r2b original.
const HONEST = {
  badge: "Built for India · tech roles first",
  intro: "Caliber scores tech jobs against your resume and shows the main reasons each one fits. It works best for software, data, DevOps, QA and IT roles in India — other fields are less tested. Apply on the company's own portal and track it all in one place.",
  scored: { title: "Resume-scored matching", body: "Each match gets a 0–100 quick score from your resume and the posting, with the main reasons shown. It is an estimate — read the posting before you apply." },
  india: { title: "India-first", body: "Remote-from-India and India-based roles first; relocation, OCI-friendly and H1B-transfer roles where the posting says so." },
  step2: "See the score and the main reasons. Matches update as new jobs are added.",
  final: "Upload your resume and get tech roles scored against your experience — with the reasons shown.",
};

// ── Marketing landing — the public front door (logged-out only) ──────────────
// Renders at "/" for guests; members get the app instead (see App.tsx). The one
// promise the product makes everywhere is preserved here: Caliber never applies
// on your behalf. Every CTA routes to /login (which hosts signup + signin).

function NavBar({ onGetStarted, onSignIn }: { onGetStarted: () => void; onSignIn: () => void }) {
  return (
    <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "18px 24px", maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      <div style={{ fontFamily: T.display, fontSize: "18px", fontWeight: 700, color: T.t0 }}>
        Caliber <span style={{ color: T.teal }}>by DevAscend</span>
      </div>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <Btn variant="ghost"   size="sm" onClick={onSignIn}>Sign in</Btn>
        <Btn variant="cta"     size="sm" onClick={onGetStarted}>Get started</Btn>
      </div>
    </header>
  );
}

function Hero({ onGetStarted, onSignIn, honest }: { onGetStarted: () => void; onSignIn: () => void; honest: boolean }) {
  const rev = useReveal(80, 16);
  return (
    <section style={{ ...rev, maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box",
      padding: "56px 24px 24px", display: "grid", gap: "40px",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "center" }}>
      <div>
        <Badge variant="teal" size="sm" pulse>{honest ? HONEST.badge : <>Built for India · remote, relocation &amp; OCI-friendly roles</>}</Badge>
        <h1 style={{ fontFamily: T.display, fontSize: "clamp(30px, 5vw, 46px)", fontWeight: 700, color: T.t0,
          lineHeight: 1.1, margin: "18px 0 0", letterSpacing: "-0.02em" }}>
          Job matches that<br/>actually <span style={{ color: T.teal }}>fit</span>.
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "16px", color: T.t1, lineHeight: 1.6, margin: "18px 0 0", maxWidth: "460px" }}>
          {honest ? HONEST.intro : <>Caliber scores every job against your resume — honestly. See exactly why each
          role fits, apply on the company's own portal, and track it all in one place.</>}
        </p>
        <div style={{ display: "flex", gap: "10px", marginTop: "28px", flexWrap: "wrap" }}>
          <Btn variant="cta"     size="lg" icon={<ArrowRight size={15} strokeWidth={2}/>} onClick={onGetStarted}>Get started free</Btn>
          <Btn variant="outline" size="lg" onClick={onSignIn}>Sign in</Btn>
        </div>
        <p style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2, marginTop: "16px", letterSpacing: "0.02em" }}>
          No spam · No auto-applying · You stay in control
        </p>
      </div>

      {/* Hero visual — a mock "pick of the week" match card */}
      <HeroCardMock />
    </section>
  );
}

function HeroCardMock() {
  const rev = useReveal(260, 18);
  return (
    <div style={{ ...rev, justifySelf: "center", width: "100%", maxWidth: "380px" }}>
      <div style={{ background: T.s1, border: `1px solid ${T.tealBdr}`, borderRadius: "14px", padding: "20px",
        position: "relative", overflow: "hidden", boxShadow: `0 8px 48px ${T.tealGlow}` }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px",
          background: `linear-gradient(90deg, transparent, ${withAlpha(T.teal, 0.56)}, transparent)` }}/>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Badge variant="teal" size="sm" pulse>pick of the week</Badge>
            <div style={{ fontFamily: T.display, fontSize: "17px", fontWeight: 700, color: T.t0, margin: "12px 0 2px" }}>
              Senior Backend Engineer
            </div>
            <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t2 }}>
              Example role · Remote (India)
            </div>
          </div>
          <div style={{ fontFamily: T.mono, fontSize: "11px", color: T.t3, textAlign: "right" }}>
            Example quick score
          </div>
        </div>
        <div style={{ padding: "11px 13px", background: T.s2, borderRadius: "8px",
          borderLeft: `2px solid ${T.teal}`, marginTop: "16px" }}>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t1, margin: 0, lineHeight: 1.55 }}>
            <span style={{ color: T.tealSoft, fontWeight: 500 }}>Why · </span>
            Quick scores use the information available in your resume and the posting. Open each role and read the posting before applying.
          </p>
        </div>
        <div style={{ display: "flex", gap: "6px", marginTop: "14px", flexWrap: "wrap" }}>
          <Badge variant="info" size="sm">remote</Badge>
          <Badge variant="default" size="sm">example</Badge>
          <Badge variant="default" size="sm">2h ago</Badge>
        </div>
      </div>
    </div>
  );
}

interface Feature { icon: React.ReactNode; title: string; body: string; }
const FEATURES: Feature[] = [
  { icon: <Gauge size={18} strokeWidth={1.8}/>,       title: "Resume-scored matching",
    body: "Every role scored 0–100 against your actual experience — with the reasoning shown, not a black box." },
  { icon: <Compass size={18} strokeWidth={1.8}/>,     title: "India-first filters",
    body: "Remote-from-India, relocation-sponsored, OCI-friendly and H1B-transfer roles, surfaced for your situation." },
  { icon: <ShieldCheck size={18} strokeWidth={1.8}/>, title: "You stay in control",
    body: "Caliber never applies on your behalf. Open the portal, apply yourself, and track the status here." },
  { icon: <FileText size={18} strokeWidth={1.8}/>,    title: "Quality over quantity",
    body: "Fewer, better-fit roles worth your time — not a spray-and-pray job board." },
];

function Features({ honest }: { honest: boolean }) {
  const rev = useReveal(120, 12);
  const features = honest
    ? FEATURES.map((f, i) => (i === 0 ? { ...f, ...HONEST.scored } : i === 1 ? { ...f, ...HONEST.india } : f))
    : FEATURES;
  return (
    <section style={{ ...rev, maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box", padding: "48px 24px" }}>
      <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {features.map((f) => (
          <div key={f.title} style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px", padding: "20px" }}>
            <div style={{ width: "34px", height: "34px", borderRadius: "8px", background: T.tealBg,
              border: `1px solid ${T.tealBdr}`, color: T.tealSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {f.icon}
            </div>
            <h3 style={{ fontFamily: T.display, fontSize: "15px", fontWeight: 600, color: T.t0, margin: "14px 0 6px" }}>{f.title}</h3>
            <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, lineHeight: 1.6, margin: 0 }}>{f.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", title: "Upload your resume", body: "We classify your target roles and seniority — the anchor for every match." },
  { n: "02", title: "Get scored matches",  body: "See fit and the reasoning, refreshed as new jobs appear across portals." },
  { n: "03", title: "Apply & track",       body: "Open the portal, apply yourself, mark it applied, and follow it through to offer." },
];

function HowItWorks({ honest }: { honest: boolean }) {
  const rev = useReveal(140, 12);
  const steps = honest ? STEPS.map((x) => (x.n === "02" ? { ...x, body: HONEST.step2 } : x)) : STEPS;
  return (
    <section style={{ ...rev, maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box", padding: "16px 24px 48px" }}>
      <div style={{ fontFamily: T.mono, fontSize: "11px", color: T.t3, letterSpacing: "0.14em",
        textTransform: "uppercase", marginBottom: "20px" }}>How it works</div>
      <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
        {steps.map((s) => (
          <div key={s.n} style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px", padding: "22px" }}>
            <div style={{ fontFamily: T.mono, fontSize: "22px", fontWeight: 700, color: T.teal }}>{s.n}</div>
            <h3 style={{ fontFamily: T.display, fontSize: "16px", fontWeight: 600, color: T.t0, margin: "10px 0 6px" }}>{s.title}</h3>
            <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, lineHeight: 1.6, margin: 0 }}>{s.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TrustBand() {
  const rev = useReveal(120, 10);
  return (
    <section style={{ ...rev, maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box", padding: "0 24px 48px" }}>
      <div style={{ background: T.tealBg, border: `1px solid ${T.tealBdr}`, borderRadius: "14px",
        padding: "28px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
        <ShieldCheck size={26} strokeWidth={1.6} color={T.tealSoft} style={{ flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: "240px" }}>
          <h3 style={{ fontFamily: T.display, fontSize: "18px", fontWeight: 700, color: T.t0, margin: "0 0 4px" }}>
            Caliber never applies on your behalf
          </h3>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, lineHeight: 1.6, margin: 0 }}>
            No mass applications, no bots submitting forms in your name. Caliber finds and
            explains the fit — you make every call and apply on the company's own portal.
          </p>
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ onGetStarted, honest }: { onGetStarted: () => void; honest: boolean }) {
  const rev = useReveal(120, 12);
  return (
    <section style={{ ...rev, maxWidth: "1080px", margin: "0 auto", width: "100%", boxSizing: "border-box",
      padding: "8px 24px 64px", textAlign: "center" }}>
      <h2 style={{ fontFamily: T.display, fontSize: "clamp(24px, 4vw, 34px)", fontWeight: 700, color: T.t0, margin: "0 0 12px" }}>
        Stop scrolling job boards.
      </h2>
      <p style={{ fontFamily: T.body, fontSize: "15px", color: T.t1, margin: "0 auto 24px", maxWidth: "440px", lineHeight: 1.6 }}>
        {honest ? HONEST.final : "Upload your resume and get roles that actually match — with the reasoning to back it up."}
      </p>
      <Btn variant="cta" size="lg" icon={<ArrowRight size={15} strokeWidth={2}/>} onClick={onGetStarted}>Get started free</Btn>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ borderTop: `1px solid ${T.b0}`, padding: "24px", maxWidth: "1080px", margin: "0 auto", width: "100%",
      boxSizing: "border-box", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
      <div style={{ fontFamily: T.display, fontSize: "13px", fontWeight: 600, color: T.t1 }}>
        Caliber <span style={{ color: T.teal }}>by DevAscend</span>
      </div>
      <div style={{ fontFamily: T.mono, fontSize: "11px", color: T.t3 }}>
        © {new Date().getFullYear()} DevAscend · Quality over quantity
      </div>
      <LegalLinks />
    </footer>
  );
}

export function Landing() {
  const navigate = useNavigate();
  const toLogin = () => navigate("/login");
  const { flags } = useLaunchConfig();
  const honest = flags.honestCopy;
  usePageMeta("Caliber — tech job matches for India, scored against your resume",
    "Upload your resume and get tech jobs in India scored 0–100 against your experience, with the reasons shown. Free. Never applies for you.");

  return (
    <div style={{ minHeight: "100dvh", background: T.bg, color: T.t0 }}>
      <NavBar onGetStarted={toLogin} onSignIn={toLogin} />
      <Hero    onGetStarted={toLogin} onSignIn={toLogin} honest={honest} />
      <Features honest={honest} />
      <HowItWorks honest={honest} />
      <TrustBand />
      <FinalCTA onGetStarted={toLogin} honest={honest} />
      <Footer />
    </div>
  );
}
